/** @format */
import React from 'react'
import styled from 'styled-components'
import { _$, Row, Col } from '@king-design/react'
import { getAgentKitRuntimeTokenOverview, getAgentKitRuntimeTokenUsage } from 'src/api/ApiAgentKit'
import { useAsync } from 'react-use'
import { AgentKitOverviewProps } from '../interface'
import OverviewLineChart, {
  OverviewLineChartPoint
} from 'src/pages/AIObservability/components/OverviewLineChart'
import { fillMetricPoints } from 'src/pages/AIObservability/ServiceOverview/container/constant'
import {
  MetricsSection,
  MetricCard,
  ChartsSection,
  ValueWithUnit,
  splitTokenValue
} from '../components'

const AgentKitToken: React.FC<AgentKitOverviewProps> = ({
  className,
  serviceName,
  startTime,
  endTime,
  region,
  step
}) => {
  const { value: metricData, loading: metricLoading } = useAsync(async () => {
    if (!region || !startTime || !endTime) {
      return
    }
    const result = await getAgentKitRuntimeTokenOverview({
      StartTime: startTime,
      EndTime: endTime,
      ServiceName: serviceName
    })
    return {
      TokenTotal: splitTokenValue(result?.TokenTotal),
      TokenInput: splitTokenValue(result?.TokenInput),
      TokenOutput: splitTokenValue(result?.TokenOutput),
      CacheToken: splitTokenValue(result?.CacheToken)
    }
  }, [region, startTime, endTime, serviceName])

  const { value: chartData, loading: chartLoading } = useAsync(async () => {
    if (!region || !startTime || !endTime) {
      return
    }
    const result = await getAgentKitRuntimeTokenUsage({
      StartTime: startTime,
      EndTime: endTime,
      Step: `${step}s`,
      ServiceName: serviceName
    })
    return (result?.Series ?? []).reduce((acc, item) => {
      acc[item.Metric] = item.Points?.length
        ? fillMetricPoints(item.Points, startTime, endTime, step)
        : []
      return acc
    }, {} as Record<string, OverviewLineChartPoint[]>)
  }, [region, startTime, endTime, step, serviceName])

  const loading = metricLoading || chartLoading

  return (
    <div className={className}>
      <MetricsSection loading={loading} aria-label={_$('指标')}>
        <MetricCard
          label={_$('Token总消耗')}
          value={
            <ValueWithUnit
              value={metricData?.TokenTotal?.value}
              unit={metricData?.TokenTotal?.unit}
            />
          }
          tips={_$('输入Token + 输出Token之和')}
        />
        <MetricCard
          label={_$('输入Token')}
          value={
            <ValueWithUnit
              value={metricData?.TokenInput?.value}
              unit={metricData?.TokenInput?.unit}
            />
          }
        />
        <MetricCard
          label={_$('输出Token')}
          value={
            <ValueWithUnit
              value={metricData?.TokenOutput?.value}
              unit={metricData?.TokenOutput?.unit}
            />
          }
        />
      </MetricsSection>
      <ChartsSection aria-label={_$('趋势图')}>
        <Row gutter="16">
          <Col span={8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('Token总消耗')}
              data={chartData?.TokenTotal}
              tokenValue
              colHeight={300}
            />
          </Col>
          <Col span={8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('输入Token')}
              data={chartData?.TokenInput}
              tokenValue
              colHeight={300}
            />
          </Col>
          <Col span={8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('输出Token')}
              data={chartData?.TokenOutput}
              tokenValue
              colHeight={300}
            />
          </Col>
        </Row>
      </ChartsSection>
    </div>
  )
}

export default styled(AgentKitToken)``
